import { resolveCityCodeFromLabel, resolveShippingDestinationCityCode } from './shippingDestinationCityCode'

type OrderDetailLike = {
  id?: number
  total?: string
  customer?: {
    first_name?: string
    last_name?: string
    email?: string
    document?: string
  }
  shippingAddress?: {
    phone?: string
    address_1?: string
    address_2?: string
    city?: string
    state?: string
  }
  lineItems?: Array<{ product_name?: string }>
} | null | undefined

/**
 * Completa campos ocultos en modo liquidar (sin cambiar la UI).
 * Misma lógica que cuando hay pedido seleccionado en Crear envío.
 */
export function enrichFormDataForQuote(
  formData: Record<string, unknown>,
  orderDetail: OrderDetailLike
): Record<string, unknown> {
  const enriched = { ...formData }

  if (orderDetail?.customer) {
    const nombrePedido = `${orderDetail.customer.first_name || ''} ${orderDetail.customer.last_name || ''}`.trim()
    if (!String(enriched.destinoNombre || '').trim() && nombrePedido) {
      enriched.destinoNombre = nombrePedido
    }
    if (!String(enriched.destinoEmail || '').trim() && orderDetail.customer.email) {
      enriched.destinoEmail = orderDetail.customer.email
    }
    if (!String(enriched.destinoNit || '').trim() && orderDetail.customer.document) {
      enriched.destinoNit = orderDetail.customer.document
    }
  }

  if (orderDetail?.shippingAddress) {
    if (!String(enriched.destinoCelular || '').trim() && orderDetail.shippingAddress.phone) {
      enriched.destinoCelular = orderDetail.shippingAddress.phone
    }
    if (!String(enriched.destinoDireccion || '').trim() && orderDetail.shippingAddress.address_1) {
      const addr2 = orderDetail.shippingAddress.address_2
      enriched.destinoDireccion = orderDetail.shippingAddress.address_1 +
        (addr2 ? `, ${addr2}` : '')
    }
  }

  if (!String(enriched.destinoNombre || '').trim()) {
    enriched.destinoNombre = 'Destinatario'
  }
  if (!String(enriched.destinoEmail || '').trim()) {
    enriched.destinoEmail = 'envio@ofima.com'
  }
  if (!String(enriched.destinoCelular || '').trim()) {
    enriched.destinoCelular = '3000000000'
  }
  if (!String(enriched.contenido || '').trim()) {
    const fromItems = orderDetail?.lineItems
      ?.map((item) => item.product_name)
      .filter(Boolean)
      .join(', ')
    enriched.contenido = fromItems || 'Mercancía general'
  }

  return enriched
}

export function assertDestinationCityForQuote(
  orderDetail: OrderDetailLike,
  formData: Record<string, unknown>
): string {
  const code = resolveShippingDestinationCityCode(orderDetail, formData)
  if (!code) {
    throw new Error(
      'Seleccione departamento y ciudad de destino para cotizar el envío'
    )
  }
  return code
}

export function getCiudadCodigoForQuote(destinoCiudad?: string): string {
  return resolveCityCodeFromLabel(destinoCiudad) || '05001'
}

export interface EnviaComQuoteShipment {
  carrier: string
  service?: string
  type?: number
}

/**
 * Payload base para POST /envia-com/quote (carriers Envia.com).
 */
export function buildEnviaComQuoteRequest(
  formData: Record<string, unknown>,
  orderDetail: OrderDetailLike,
  shipment: EnviaComQuoteShipment
) {
  const destinoCodigo = getCiudadCodigoForQuote(String(formData.destinoCiudad || ''))
  const peso = Number(formData.peso) < 1 ? 1 : Number(formData.peso)
  const valorDeclarado = Number(formData.valorDeclarado) || 0

  const packages: Record<string, unknown> = {
    amount: 1,
    type: 'box',
    content: formData.contenido || 'Mercancía general',
    quantity: 1,
    weight: peso,
    weightUnit: 'KG',
    declaredValue: valorDeclarado,
    insurance: valorDeclarado,
    dimensions: {
      length: Math.round(Number(formData.largo)),
      height: Math.round(Number(formData.altura)),
      width: Math.round(Number(formData.ancho))
    }
  }

  if (formData.metodoPago === 'cod' && orderDetail?.total) {
    packages.additionalServices = [{
      service: 'cash_on_delivery',
      data: {
        amount: typeof orderDetail.total === 'string'
          ? orderDetail.total
          : String(orderDetail.total),
        currency: 'COP'
      }
    }]
  }

  return {
    origin: {
      name: formData.origenNombre,
      company: formData.origenEmpresa,
      email: formData.origenEmail,
      phone: formData.origenCelular,
      country: 'CO',
      state: 'AN',
      province: 'Antioquia',
      city: '05001',
      street: formData.origenDireccion,
      number: formData.origenDireccion,
      postalCode: '05001',
      identification: {
        type: 'CC',
        number: formData.origenDocumento || formData.origenNit || '123456789'
      }
    },
    destination: {
      name: formData.destinoNombre,
      company: formData.destinoEmpresa || '',
      email: formData.destinoEmail,
      phone: formData.destinoCelular,
      country: 'CO',
      state: 'AN',
      province: formData.destinoDepartamento || 'Antioquia',
      city: destinoCodigo,
      street: formData.destinoDireccion,
      number: formData.destinoDireccion,
      postalCode: destinoCodigo,
      identification: {
        type: 'CC',
        number: formData.destinoDocumento || formData.destinoNit || '123456789'
      }
    },
    packages: [packages],
    shipment: {
      carrier: shipment.carrier,
      ...(shipment.service ? { service: shipment.service } : {}),
      type: shipment.type ?? 1
    }
  }
}
